import { AfterViewInit, Component, ElementRef, HostBinding, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { HelpdeskOverlayService } from 'src/app/service/helpdesk-overlay.service';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AuthService } from '../../service/auth.service';
import { InfoService } from '../../service/info.service';
import { SidebarService } from '../../service/sidebar.service';
import { FixedTopProgressBarComponent } from '../fixed-top-progress-bar/fixed-top-progress-bar.component';
import { ThemePalette } from '@angular/material/core';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NotificationService } from '../../service/notification.service';
import { SearchButtonComponent } from '../search-button/search-button.component';
import { NavbarService } from 'src/app/service/navbar.service';
import { SidebarItem } from 'src/app/model/sidebar-item';
import { User } from 'src/app/model/user';
import { Location } from '@angular/common';
import { UserService } from 'src/app/service/user.service';
import { CommandPaletteComponent } from '../command-palette/command-palette.component';
import { AvatarProfileComponent } from '../avatar-profile/avatar-profile.component';

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss']
})
export class NavbarComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() color: ThemePalette;
  @ViewChild(SearchButtonComponent, { static: true }) searchButton: SearchButtonComponent;
  @ViewChild(FixedTopProgressBarComponent, { static: true }) progressBar: FixedTopProgressBarComponent;

  // Indicador deslizante do menu (Eventos / Submissões / etc.). O `<nav>` é o
  // container relativo; o `<span>` ancora a "pílula" que se move entre itens.
  @ViewChild('navItems') navItemsRef?: ElementRef<HTMLElement>;
  @ViewChild('navIndicator') navIndicatorRef?: ElementRef<HTMLElement>;
  @ViewChild('navUnderline') navUnderlineRef?: ElementRef<HTMLElement>;
  @ViewChild('avatarProfile') avatarProfile!: AvatarProfileComponent;
  @ViewChild('navUserMenuBtn') navUserMenuBtn!: ElementRef<HTMLSpanElement>;

  // Referência ao componente do sino — permite abrir o dialog de
  // notificações programaticamente a partir do menu do usuário mobile.
  @ViewChild('bellRef') bellRef?: { openDialog(ev: Event): void };
  // Trigger do menu hamburger — usamos pra FECHAR o menu durante
  // resize da janela (a posição/existência dos items muda no meio).
  @ViewChild('overflowTrigger') overflowTrigger?: MatMenuTrigger;

  @HostBinding('class.hidden') isHidden = false;

  icon = 'fa-bars';

  notificationMenu: MatMenu;

  isAvatarProfileOpen : Boolean = false;

  // `items` é populado assincronamente pelo SidebarComponent (após
  // getUserFromToken). Reposicionamos o indicador sempre que o input mudar
  // para refletir adição/remoção de papéis. Recomputa overflow também.
  private _items: SidebarItem[];
  @Input() set items(value: SidebarItem[]) {
    this._items = value;
    setTimeout(() => this.updateNavIndicator(), 0);
  }
  get items(): SidebarItem[] { return this._items; }

  
  // ─── Overflow menu (hamburger) ─────────────────────────────────────
  // Rotas que PODEM ser absorvidas pro menu hamburger, em ORDEM (primeira
  // = primeira a sumir da nav quando encolhe). "Eventos" é a única rota
  // primária que permanece sempre na barra; as demais podem ser absorvidas
  // progressivamente. Renderizadas SEMPRE no menu; o estado calculado abaixo
  // decide quem fica visível na nav e quem aparece no menu.
  private static readonly OVERFLOW_ROUTES = [
    '/my-events',             // Eventos que coordeno (1º a ser absorvido)
    '/committees',            // TPCs que participo
    '/publication-chairings', // Publicações que coordeno
    '/session-chairings',     // Sessões que coordeno
    '/reviews',               // Minhas revisões
    '/submissions'            // Minhas submissões (último a ser absorvido)
  ];

  // Items renderizados no menu hamburger (subset de `items`).
  public get overflowItems(): SidebarItem[] {
    if (!this._items) { return []; }
    const set = new Set(NavbarComponent.OVERFLOW_ROUTES);
    return this._items.filter(i => set.has(i.link));
  }

  // Rotas ATUALMENTE absorvidas (state Angular). Espelha a decisão do
  // `applyOverflow` — mat-menu items usam `[class.is-nav-visible]` pra
  // esconder os items que ainda estão na nav.
  public overflowedRoutes = new Set<string>();
  public isRouteInNav(link: string): boolean {
    return !this.overflowedRoutes.has(link);
  }

  // Type-narrowing helpers pro template — SidebarItem.icon/label são
  // union (string | object), o template só usa a variante object.
  public asIcon(icon: any): { name: string, font: string } | null {
    return icon && typeof icon === 'object' ? icon : null;
  }
  public asLabel(label: any): { full: string, short: string } | null {
    return label && typeof label === 'object' ? label : null;
  }

  // Aplica a classe `tpc-user-menu` no painel do menu hamburger após abrir.
  // O mat-menu do Material 11 não aceita `[panelClass]` como binding, então
  // fazemos manualmente via query no CDK overlay pane logo após a abertura.
  public onOverflowMenuOpened(): void {
    // Aguarda o painel entrar no DOM (portaled ao <body>).
    requestAnimationFrame(() => {
      const panels = document.querySelectorAll('.mat-menu-panel');
      panels.forEach(p => p.classList.add('tpc-user-menu'));
      // Aplica a linha accent aos items SELECIONADOS do menu overflow
      // — mesma shape/gradient da underline da nav (via helpers
      // NavbarComponent.buildAccentClipPath/Gradient). Item de menu
      // pode ter só um selecionado por vez (routerLinkActive), então
      // itera todos e ativa o `::after` via inline vars nos que estão
      // com `.is-selected`.
      panels.forEach(panel => {
        const items = panel.querySelectorAll<HTMLElement>('.nav-overflow-mi.is-selected');
        items.forEach(item => this.paintOverflowAccent(item));
      });
    });
  }

  /**
   * Pinta a linha accent num item do menu overflow. Usa os MESMOS
   * helpers do accent da underline da nav (fonte única). Escrita via
   * CSS custom properties consumidas pelo `::after` do item.
   */
  private paintOverflowAccent(item: HTMLElement): void {
    // `offsetWidth` (não `getBoundingClientRect().width`) porque o painel
    // do mat-menu ANIMA COM `transform: scale(...)` — o rect vem escalado
    // durante a animação e a linha nasce curta. `offsetWidth` ignora
    // transforms e devolve a largura de layout final.
    const layoutW = item.offsetWidth;
    // Inset horizontal de 2px cada lado — casa com o inset da nav
    // (1.5px lá + 0.5px de folga aqui). Antes eram 8px de cada lado
    // e a linha ficava muito curta em items estreitos.
    const inset = 2;
    const uw = Math.max(0, layoutW - inset * 2);
    if (uw <= 0) { return; }
    const iconEl = item.querySelector<HTMLElement>('.mat-icon');
    const accent = iconEl ? getComputedStyle(iconEl).color : '';
    if (!accent || accent === 'rgba(0, 0, 0, 0)' || accent === 'transparent') { return; }
    // Border color: item de menu não tem borda visível, então usamos
    // a cor de background do PRÓPRIO menu (bg é o "lado" da linha) —
    // dá a impressão de fade sem depender de linha visual.
    const menuPanel = item.closest('.mat-menu-panel') as HTMLElement | null;
    const borderColor = menuPanel ? getComputedStyle(menuPanel).backgroundColor : 'transparent';
    item.style.setProperty('--accent-width', `${uw}px`);
    item.style.setProperty('--accent-inset', `${inset}px`);
    item.style.setProperty('--accent-clip-path', NavbarComponent.buildAccentClipPath(uw, 'bottom'));
    item.style.setProperty('--accent-background', NavbarComponent.buildAccentGradient(accent, borderColor));
  }
  searchIsOpen = false;
  userIsAdmin = false;
  user: User;

  // Hint do atalho: Mac mostra ⌘K, demais OSes mostram Ctrl+K.
  readonly shortcutHint = this.detectMac() ? '⌘K' : 'Ctrl+K';

  private paletteRef?: MatDialogRef<CommandPaletteComponent>;

  // True quando o command palette está aberto — usado pra preencher o ícone
  // da lupa (`.menu-open` no template).
  public get isPaletteOpen(): boolean {
    return !!this.paletteRef;
  }



  // True quando o card de helpdesk está aberto — usado pra preencher o
  // ícone do headset. Vem do `HelpdeskOverlayService` (Subject), espelhado
  // em ngOnInit pra ser template-bindable sem async pipe.
  isSupportCardOpen = false;

  constructor(
    public authService: AuthService,
    public sidebarService: SidebarService,
    public navbarService: NavbarService,
    public infoService: InfoService,
    private notificationService: NotificationService,
    private userService: UserService,
    public location: Location,
    private dialog: MatDialog,
    private router: Router,
    private helpdeskOverlay: HelpdeskOverlayService,
  ) {}

  ngOnInit(): void {
    this.authService.directUserIsAdmin().subscribe(userIsAdmin => {
      this.userIsAdmin = userIsAdmin;
    });
    this.userService.getUserFromToken().subscribe( user => {
      this.user = user;
    });
    // Atualiza a posição do indicador após cada navegação (a classe `.selected`
    // dos itens recalcula em ngOnInit deles via `window.location.pathname`).
    this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => {
      // Agenda na próxima task para que o Angular já tenha aplicado a classe
      // .selected no item correto antes de medirmos o retângulo.
      setTimeout(() => this.updateNavIndicator(), 0);
    });
    this.helpdeskOverlay.isOpen$.subscribe(open => {
      this.isSupportCardOpen = open;
    });
  }

  ngAfterViewInit(): void {
    // Posiciona o indicador na carga inicial. Pequeno delay porque os itens
    // são `@Input items` e podem ainda não ter renderizado.
    setTimeout(() => {
      this.applyOverflow();
      this.updateNavIndicator();
    }, 0);

    // ResizeObserver: recomputa overflow toda vez que a nav muda de largura
    // (window resize, sidebar do chair abrindo/fechando, etc.).
    const nav = this.navItemsRef?.nativeElement;
    if (nav && typeof ResizeObserver !== 'undefined') {
      this._navResizeObs = new ResizeObserver(() => {
        this.applyOverflow();
        requestAnimationFrame(() => this.updateNavIndicator());
      });
      this._navResizeObs.observe(nav);
    }

    // Theme change: a cor accent da linha do item ativo é LIDA via
    // `getComputedStyle(...)` no momento em que a barra reposiciona
    // (updateNavIndicator). Se o usuário troca o tema sem navegar, a
    // linha fica com a cor antiga cacheada no `element.style`. Observa
    // mudanças de classe em `document.body` (onde `.dark-theme` entra/
    // sai) e re-executa a atualização.
    if (typeof MutationObserver !== 'undefined' && document.body) {
      this._themeObs = new MutationObserver(muts => {
        if (muts.some(m => m.attributeName === 'class')) {
          requestAnimationFrame(() => this.updateNavIndicator());
        }
      });
      this._themeObs.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    }
  }

  ngOnDestroy(): void {
    this._navResizeObs?.disconnect();
    this._themeObs?.disconnect();
    // Limpa o flag global — foi setado pelo `applyOverflow` desta navbar
    // (autor/revisor); se a navbar sai da tela (ex.: usuário navegou pro
    // chair, cuja shell é o `admin-event-sidebar`), o flag não deve ficar
    // pendurado no `<html>`, senão o menu do avatar (que respeita o gate
    // `html[data-chrome-collapsed]`) mostraria a linha de search/help/bell
    // indevidamente no chair — sem uma navbar de autor/revisor pra dar
    // significado ao "colapso". Simétrico ao set inicial em `applyOverflow`.
    delete document.documentElement.dataset.chromeCollapsed;
  }
  private _navResizeObs?: ResizeObserver;
  private _themeObs?: MutationObserver;

  // ─── Overflow logic (JS via scrollWidth) ─────────────────────────────
  // Estratégia:
  //   1. Reset: mostra tudo (remove `.is-overflowed` de todos).
  //   2. Se `scrollWidth <= clientWidth` → cabe, hamburger some. Fim.
  //   3. Se overflow → hamburger aparece, absorve items em ORDEM DE
  //      PRIORIDADE (Coord → TPCs → Sessions) até caber.
  // Manipulação DIRETA no DOM (via classList) — sem passar por Angular CD.
  private applyOverflow(): void {
    const nav = this.navItemsRef?.nativeElement;
    if (!nav || !this._items) { return; }

    // Mobile-portrait usa bottom-nav em layout próprio — bail.
    if (document.documentElement.dataset.device === 'mobile-portrait') {
      return;
    }

    const hamburger = nav.querySelector('.navbar-overflow-btn') as HTMLElement | null;
    const itemEls = Array.from(nav.querySelectorAll('app-sidebar-item')) as HTMLElement[];

    // Reset — mostra tudo e esconde hamburger.
    itemEls.forEach(el => el.classList.remove('is-overflowed'));
    hamburger?.classList.remove('is-visible');
    // Reflow síncrono pra recalcular scrollWidth.
    void nav.offsetHeight;

    // Set local pra acumular decisões e sincronizar com Angular só no fim.
    const absorbed = new Set<string>();

    // Usa soma de offsetWidth (items + hamburger) — o indicator
    // position:absolute fica travado no width do último item selected,
    // contribuindo pro scrollWidth mesmo quando o item selected está
    // hidden; soma direta ignora ele. O padding do container é ignorado
    // (items podem se sobrepor a ele visualmente sem quebrar o layout).
    // Gap de segurança: sem ele, items encostam no hamburger (acavalam);
    // com padding cheio do container (32px) dispara cedo demais.
    const SAFETY_GAP = 8;
    const measureItemsWidth = () => {
      const itemsSum = Array.from(itemEls)
        .reduce((s, el) => s + el.offsetWidth, 0);
      const hbW = hamburger?.offsetWidth || 0;
      return itemsSum + hbW + SAFETY_GAP;
    };

    if (measureItemsWidth() > nav.clientWidth) {
      // Overflow — mostra hamburger e começa a absorver.
      hamburger?.classList.add('is-visible');
      void nav.offsetHeight;

      const priority = NavbarComponent.OVERFLOW_ROUTES;
      for (const route of priority) {
        const idx = this._items.findIndex(i => i.link === route);
        if (idx < 0) { continue; }
        const el = itemEls[idx];
        if (!el) { continue; }
        el.classList.add('is-overflowed');
        absorbed.add(route);
        void nav.offsetHeight;
        if (measureItemsWidth() <= nav.clientWidth) { break; }
      }
    }

    // Sincroniza com o state Angular (só se mudou) — dispara CD dos menu
    // items, atualizando quem tem `is-nav-visible`.
    const same = absorbed.size === this.overflowedRoutes.size &&
      [...absorbed].every(r => this.overflowedRoutes.has(r));
    if (!same) {
      this.overflowedRoutes = absorbed;
      // Reposiciona/oculta a pílula+linha quando o item selected acabou
      // de trocar de lado (nav ↔ hamburger) — sem esta chamada a pílula
      // fica apontando pro item agora hidden.
      requestAnimationFrame(() => this.updateNavIndicator());
    }
  }
  

  @HostListener('window:resize')
  onResize(): void {
    // Reposiciona sem animar — `transition` aplicaria delay incômodo a cada
    // resize. Desativa transição momentaneamente.
    const ind = this.navIndicatorRef?.nativeElement;
    if (!ind) { return; }
    const prev = ind.style.transition;
    ind.style.transition = 'none';
    // Fecha o hamburger menu — items podem mover pra dentro/fora e o menu
    // ancorado ficaria com content ou posição errados durante o drag.
    this.overflowTrigger?.closeMenu();
    // Recompute overflow — .navbar-items tem flex-grow: 0, então quando
    // a janela AUMENTA ela não cresce e o ResizeObserver não dispara.
    // window:resize é o único trigger confiável pra "des-absorver" items.
    this.applyOverflow();
    this.updateNavIndicator();
    requestAnimationFrame(() => { ind.style.transition = prev; });
  }

  private updateNavIndicator(): void {
    const nav = this.navItemsRef?.nativeElement;
    const ind = this.navIndicatorRef?.nativeElement;
    const und = this.navUnderlineRef?.nativeElement;
    if (!nav || !ind) { return; }

    const selected = nav.querySelector('.sidebar-item.selected') as HTMLElement | null;
    // Item pode estar "selected" mas absorvido pelo hamburger
    // (display:none via .is-overflowed) — trata como "não tem selected na
    // barra": esconde pílula/linha sem animar. Consequência: qualquer
    // transição envolvendo um destino/origem absorvido cai no branch
    // "wasActive=false" da PRÓXIMA chamada e pula a animação — exatamente
    // a regra "só animar se AMBOS estiverem na barra".
    const selectedVisible = !!selected
      && !selected.classList.contains('is-overflowed')
      && selected.offsetWidth > 0;
    if (!selected || !selectedVisible) {
      ind.classList.remove('is-active');
      und?.classList.remove('is-active');
      return;
    }

    // Se nenhum item estava selecionado antes (`is-active` ausente), pulamos
    // a transição — a pílula apareceria deslizando "do nada" pra posição do
    // novo item, o que é visualmente confuso.
    const wasActive = ind.classList.contains('is-active');
    const prevIndTr = wasActive ? null : ind.style.transition;
    const prevUndTr = wasActive || !und ? null : und.style.transition;
    if (!wasActive) {
      ind.style.transition = 'none';
      if (und) { und.style.transition = 'none'; }
    }

    // Em mobile-portrait (bottom nav) o "target visual" é a .mat-icon
    // (pill wraps só o ícone), não o item inteiro. Desktop mantém item
    // como target — mesma lógica, target trocado.
    const isMobilePortrait = document.documentElement.dataset.device === 'mobile-portrait';
    const target = (isMobilePortrait
      ? selected.querySelector('.mat-icon') as HTMLElement | null
      : null) || selected;

    const navRect = nav.getBoundingClientRect();
    const itemRect = target.getBoundingClientRect();
    const x = itemRect.left - navRect.left;
    const y = itemRect.top - navRect.top;

    ind.style.transform = `translate(${x}px, ${y}px)`;
    ind.style.width = `${itemRect.width}px`;
    ind.style.height = `${itemRect.height}px`;
    ind.classList.add('is-active');

    // Underline — irmã da pílula (não filha), posicionamento em
    // transform + width espelhando a geometria do item selecionado.
    // Desktop: ancorada na borda INFERIOR do item. Mobile-portrait:
    // ancorada no TOPO do item. Cor lida do `::after` do item (desktop)
    // ou do `color` da `.mat-icon` (mobile).
    if (und) {
      const underlineY = isMobilePortrait
        ? y
        : y + itemRect.height - 2;
      // Inset lateral de 1.5px de cada lado — compensa o border do
      // item (1px) + 0.5px de folga sub-pixel.
      const uw = itemRect.width - 3;
      und.style.transform = `translate(${x + 1.5}px, ${underlineY}px)`;
      und.style.width = `${uw}px`;
      und.style.clipPath = NavbarComponent.buildAccentClipPath(uw, isMobilePortrait ? 'top' : 'bottom');
      und.classList.add('is-active');

      const accent = isMobilePortrait
        ? getComputedStyle(target).color
        : getComputedStyle(selected, '::after').backgroundColor;
      if (accent && accent !== 'rgba(0, 0, 0, 0)' && accent !== 'transparent') {
        const borderColor = getComputedStyle(selected).borderTopColor;
        und.style.background = NavbarComponent.buildAccentGradient(accent, borderColor);
        und.style.backgroundColor = '';
      }
    }

    if (!wasActive) {
      requestAnimationFrame(() => {
        ind.style.transition = prevIndTr || '';
        if (und) { und.style.transition = prevUndTr || ''; }
      });
    }
  }

  toggle(open: boolean = this.isHidden): void {
    this.isHidden = !open;
  }

  toggleSidebar(): void {
    this.sidebarService.toggle();
  }

  get isMobile(): boolean {
    return this.sidebarService.sidebar.isMobile;
  }

  searchToggled(event: { status: boolean }) {
    this.searchIsOpen = event.status;
  }

  // Toggle do card de ajuda/suporte via CDK Overlay (HelpdeskOverlayService).
  // Mesmo stack do mat-menu do sino — backdrop transparente, ESC, reflow
  // em scroll/resize. Posicionamento ancorado ao próprio botão clicado.
  openSupportHelp(event?: MouseEvent): void {
    const target = (event?.currentTarget ?? event?.target) as HTMLElement | undefined;
    const trigger = target?.closest?.('.support-help-button') as HTMLElement | null;
    // Sem evento (chamado do menu do avatar em mobile-portrait) — o botão
    // do topo está `display:none`, então ancoramos no user-menu-button.
    const supportBtn = document.querySelector(
      'app-navbar .navbar .right-navbar-container .support-help-button'
    ) as HTMLElement | null;
    const isSupportVisible = !!supportBtn?.offsetParent;
    const fallback = isSupportVisible ? supportBtn : (document.querySelector(
      'app-navbar .navbar .right-navbar-container .user-menu-button'
    ) as HTMLElement | null);
    this.helpdeskOverlay.toggle(trigger ?? target ?? fallback);
  }

  // Chamado do (notificationsClick) do avatar-profile — abre o dialog
  // de notificações via o próprio componente do sino. Em mobile-portrait,
  // é o único caminho pro sino (o botão do topo está oculto).
  openNotificationsFromMenu(): void {
    // Evento sintético — o openDialog só usa `stopPropagation()`.
    const fakeEvt = new Event('click');
    this.bellRef?.openDialog(fakeEvt);
  }

  openCommandPalette(): void {
    if (this.paletteRef) {
      return;
    }
    this.paletteRef = this.dialog.open(CommandPaletteComponent, {
      panelClass: 'command-palette-panel',
      width: '560px',
      maxWidth: '92vw',
      autoFocus: false,
      restoreFocus: true,
      position: { top: '15vh' },
    });
    this.paletteRef.afterClosed().subscribe(() => {
      this.paletteRef = undefined;
    });
  }

  openAvatarMenu(): void{
    this.avatarProfile.openMenu(this.navUserMenuBtn.nativeElement);
    this.isAvatarProfileOpen=true;
    this.avatarProfile.closed.subscribe(()=>{
      this.isAvatarProfileOpen=false;
    });
  }

  // Atalho global: Cmd+K (Mac) ou Ctrl+K (demais). Ignora se um dialog/input
  // já consumiu a tecla, mas como capturamos no document, basta verificar
  // que não estamos digitando em um input próprio do palette (que fica em
  // outro contexto de evento porque o palette é um overlay).
  @HostListener('document:keydown', ['$event'])
  onDocKey(event: KeyboardEvent): void {
    const isModK = (event.metaKey || event.ctrlKey) && (event.key === 'k' || event.key === 'K');
    if (isModK) {
      event.preventDefault();
      this.openCommandPalette();
    }
  }

  private detectMac(): boolean {
    return typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
  }

  // ─── Accent line: helpers compartilhados ───────────────────────────────
  // Fonte única de verdade pra shape+cor da linha accent do item
  // selecionado. Consumidores: (1) a underline da pílula da nav
  // (updateNavIndicator, dinâmica), (2) os items do menu overflow
  // (aplicados no onOverflowMenuOpened, estáticos). Tweaks aqui refletem
  // nos dois lugares.
  private static readonly ACCENT_LINE_HEIGHT = 2;      // px
  private static readonly ACCENT_CORNER_RX  = 5;       // px — raio horizontal do arco elíptico do canto
  private static readonly ACCENT_TAIL_PX    = 6;       // px — extensão da mistura accent↔border em cada ponta

  /**
   * Monta o `clip-path: path()` da linha accent. `side='bottom'` deixa
   * os cantos INFERIORES arredondados (curva pra dentro, "afinando" as
   * pontas da base). `side='top'` espelha (mobile-portrait / bottom nav).
   */
  public static buildAccentClipPath(uw: number, side: 'bottom' | 'top' = 'bottom'): string {
    const uh = NavbarComponent.ACCENT_LINE_HEIGHT;
    const rx = NavbarComponent.ACCENT_CORNER_RX;
    const ry = uh;
    return side === 'top'
      ? `path("M 0 ${uh} L ${uw} ${uh} L ${uw} ${ry} A ${rx} ${ry} 0 0 0 ${uw - rx} 0 L ${rx} 0 A ${rx} ${ry} 0 0 0 0 ${ry} Z")`
      : `path("M 0 0 L ${uw} 0 L ${uw} ${uh - ry} A ${rx} ${ry} 0 0 1 ${uw - rx} ${uh} L ${rx} ${uh} A ${rx} ${ry} 0 0 1 0 ${uh - ry} Z")`;
  }

  /**
   * Monta o `background: linear-gradient(...)` que faz o accent puro no
   * meio virar mistura 50/50 com a cor da borda do botão nas pontas.
   */
  public static buildAccentGradient(accent: string, borderColor: string): string {
    const tail = NavbarComponent.ACCENT_TAIL_PX;
    const mix = `color-mix(in srgb, ${accent}, ${borderColor})`;
    return `linear-gradient(to right, ${mix} 0px, ${accent} ${tail}px, ${accent} calc(100% - ${tail}px), ${mix} 100%)`;
  }
}
